import {
  type ChatwootAttributes,
  ChatwootClient,
} from '@fordeer/chatwoot-widget'

const APP_NAME = 'Invoice'

/** Minimal shop profile shape feeding Chatwoot (from whoami endpoint → subscription). */
export interface InvoiceShopInfo {
  shopify_domain?: string
  shop_email?: string
  name?: string
  plan?: string
  country_name?: string
  shop_id?: number | string
  type_app?: string
  billing_cycle?: string
  chatwoot_identifier?: string
  chatwoot_identifier_hash?: string
}

const compactAttributes = (
  attributes: ChatwootAttributes,
): ChatwootAttributes =>
  Object.fromEntries(
    Object.entries(attributes).filter(
      ([, value]) => value !== '' && value !== null,
    ),
  )

const client = new ChatwootClient({
  baseUrl:
    import.meta.env.VITE_CHATWOOT_BASE_URL || 'https://chat.fordeercommerce.io',
  websiteToken:
    import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN || 'w2hwvBgPjjKtbfpdE4gEH4d3',
  readinessTimeoutMs: 25_000,
  settings: {
    hideMessageBubble: true,
    position: 'right',
    locale: 'en',
    type: 'standard',
    launcherTitle: 'Chat with us',
  },
})

/**
 * Warm up the Chatwoot SDK (script + widget load) as early as possible so the
 * provider decision does not have to wait for the SDK round-trip.
 */
export const warmUpChatwoot = (): Promise<boolean> => client.initialize()

export const setChatwootShopAttributes = (
  shop: InvoiceShopInfo,
  additionalBenefits?: string,
): Promise<boolean> =>
  client.setContactAttributes(
    compactAttributes({
      store_domain: shop.shopify_domain ? `https://${shop.shopify_domain}` : '',
      store_email: shop.shop_email || '',
      shopify_plan: shop.plan || '',
      country: shop.country_name || '',
      shop_id: shop.shop_id != null ? String(shop.shop_id) : '',
      app_name: APP_NAME,
      subscription_plan: shop.type_app || '',
      subscription_billing_interval: shop.billing_cycle || '',
      ...(additionalBenefits === undefined
        ? {}
        : { additional_benefits: additionalBenefits }),
    }),
  )

export const initializeChatwootSupport = async (
  shop: InvoiceShopInfo,
  additionalBenefits?: string,
): Promise<boolean> => {
  const identifier = shop.chatwoot_identifier?.trim()
  const identifierHash = shop.chatwoot_identifier_hash?.trim()
  if (!identifier || !identifierHash) return false
  if (!(await client.initialize())) return false
  if (
    !(await client.setUser(identifier, {
      identifier_hash: identifierHash,
      name: shop.name || shop.shopify_domain,
      ...(shop.shop_email ? { email: shop.shop_email } : {}),
    }))
  ) {
    return false
  }
  if (!(await setChatwootShopAttributes(shop, additionalBenefits))) return false
  return await client.show()
}

export const openChatwootSupport = async (
  attributes: ChatwootAttributes,
): Promise<boolean> => {
  const populatedAttributes = compactAttributes(attributes)

  if (Object.keys(populatedAttributes).length) {
    await client.setConversationAttributes(populatedAttributes)
  }
  return client.open()
}

export const hideChatwootSupport = (): Promise<boolean> => client.hide()

export const showChatwootSupport = (): Promise<boolean> => client.show()

export const popoutChatwootSupport = async (
  attributes: ChatwootAttributes,
): Promise<boolean> => {
  const populatedAttributes = compactAttributes(attributes)

  if (Object.keys(populatedAttributes).length) {
    await client.setConversationAttributes(populatedAttributes)
  }
  return client.popout()
}
